import {BettingTypesMap} from "features/core/definitions";

// Ekrandaki seçim bağlamının hâlâ oynanabilir olup olmadığını söyleyen SAF katman.
// Bülten 5 saniyede bir tazeleniyor ve kapanma bir olay olarak gelmiyor: kart `status`ünü
// kaybediyor, tür `wager_details`ten düşüyor, koşu türün `runs`undan çıkıyor. Sayfa bunu
// kendi başına fark etmediği sürece kullanıcı kapanmış bir bağlamda oynamaya devam ediyor.
//
// İki tüketici, iki ayrı politika (sepet modelleri farklı olduğu için):
//   * Müşterek  - kupon (kart+tür+alt tür) bağlamına kilitli, taşımak seçimleri GİZLER.
//                 Bu yüzden bağlam kırılınca modal + sıfırlama.
//   * Sabit ihtimalli - sepet bağlam-bağımsız (çok kartlı/çok koşulu), taşımak hiçbir şeyi
//                 gizlemez. Bu yüzden sessiz geçiş; sepetteki geçersiz bacak AYRICA
//                 satırında işaretlenir.
//
// 4nala karşılığı: web-4nala-com/src/features/betslip/lib/selection-guard.ts. Oradaki FOB
// sayfası LİSTE biçimli olduğu için koşu kademesi yok; eküri'de tek kart + koşu sekmeleri
// olduğundan merdiven üç kademe.

/** Seçim bağlamının kırıldığı ilk kademe. */
export type SelectionBreak = "ok" | "run" | "type" | "card";

/**
 * Kart hâlâ ilgili listede mi. Bülten haritaları kapalı kartları da taşıdığı için
 * "kart çözülüyor mu" yeterli bir ölçüt DEĞİL; kartın oynanabilir listede durması gerekiyor.
 */
export function cardStillListed(id: any, list: any[]): boolean {
    return id != null && list.some((el: any) => el._id === id);
}

/** `wager_details[].runs` her iki biçimde de gelebiliyor (dizi ya da "1,2,3"). */
export function wagerRunNos(wager: any): number[] {
    const raw = Array.isArray(wager?.runs)
        ? wager.runs
        : String(wager?.runs ?? "").split(",").filter((r: string) => r !== "");
    return raw.map((r: any) => Number(r));
}

/** Müşterek bağlamı: kart → tür → alt tür (ayak grubunun başlangıç koşusu). */
export function mutualSelectionBreak(params: {
    card: any;
    availableCards: any[];
    typeId: any;
    subTypeId: any;
}): SelectionBreak {
    const {card, availableCards, typeId, subTypeId} = params;
    if (!card || !cardStillListed(card._id, availableCards)) {
        return "card";
    }
    const catalogType: any = BettingTypesMap[Number(typeId)];
    const wager = (card.wager_details ?? []).find((w: any) => Number(w.type) === Number(typeId));
    if (!wager || !catalogType || catalogType.isFob || catalogType.available !== true) {
        return "type";
    }
    if (subTypeId == null || !wagerRunNos(wager).includes(Number(subTypeId))) {
        return "run";
    }
    return "ok";
}

// --- sabit ihtimalli ------------------------------------------------------------------

/**
 * Bir oran girdisi seçilebilir mi. `isFobItemPlayable` ile AYNI kural.
 * **KO burada GEÇERLİ**: kart açılışında TJK oranı henüz açıklamamıştır, girdi
 * `status:'o'` + `win_odds` 1.00 gelir ve bahis oynanabilir. Kapanmış koşu bu değil,
 * kilittir (`show:false` / `bettable:false`). İkisi ham veride birbirine çok benziyor -
 * ayıran tek alan `status`. Karıştırılırsa kart açılışında (her koşu KO) kullanıcı
 * kartın sonuna süpürülür.
 */
export function isOddsEntrySelectable(entry: any): boolean {
    if (entry?.show === false || entry?.bettable === false) {
        return false;
    }
    const hasLiveOdds = entry?.show && entry?.win_odds && Number(entry.win_odds) > 1;
    return !!(hasLiveOdds || entry?.status === "o");
}

/** Koşu, kartın tarifesinde bu FOB türü için açılmış mı. */
export function isRunEligibleForFobBettingType(hippodrome: any, run: any, bettingTypeId: number): boolean {
    const wager = hippodrome?.wager_details?.find((w: any) => Number(w.type) === bettingTypeId);
    if (!wager) return false;
    return wagerRunNos(wager).includes(Number(run?.no));
}

export function fobOddsEntry(run: any, bettingTypeId: any, oddKey: any): any {
    if (!BettingTypesMap[Number(bettingTypeId)]?.isFavoriteExcluded) {
        return run?.event_odds?.[oddKey];
    }
    if (!run?.fob_ww_status) return undefined;
    const horseNo = String(oddKey).replace('WW', '');
    return Object.values<any>(run.fob_ww ?? {}).flat().find((e: any) => String(e?.horse_no) === horseNo);
}

export function fobExcludedHorseNo(run: any): string | null {
    const entry = Object.values<any>(run?.fob_ww ?? {}).flat().find((e: any) => e?.excluded_horse != null);
    return entry ? String(entry.excluded_horse) : null;
}

export function runHasH2HMarket(run: any): boolean {
    return !!(run?.fob_h2h_status && run?.fob_h2h && Object.keys(run.fob_h2h).length > 0);
}

/**
 * Koşuda bu türde oynanacak bir şey kaldı mı. `fob_wager_status` BİLEREK sorulmuyor:
 * o alan "canlı bahis akışı var mı" demek, koşunun açık olup olmadığı değil.
 */
export function runPlayableForFobType(card: any, run: any, type: any): boolean {
    if (!type || !run) return false;
    if (type.isHeadToHead) return runHasH2HMarket(run);
    if (type.isFavoriteExcluded) {
        return !!run.fob_ww_status && Object.values<any>(run.fob_ww ?? {}).flat().some(isOddsEntrySelectable);
    }
    if (!isRunEligibleForFobBettingType(card, run, type.id)) return false;
    const odds = run.event_odds ?? {};
    return Object.values(odds).some(isOddsEntrySelectable);
}

/** Sabit ihtimalli bağlamı: kart → tür → koşu. */
export function fobSelectionBreak(params: {
    card: any;
    fobCards: any[];
    type: any;
    runNo: any;
}): SelectionBreak {
    const {card, fobCards, type, runNo} = params;
    if (!card || !cardStillListed(card._id, fobCards)) {
        return "card";
    }
    const wager = (card.wager_details ?? []).find((w: any) => Number(w.type) === Number(type?.id));
    if (!type || !wager) return "type";
    const run = (card.runs ?? []).find((r: any) => Number(r.no) === Number(runNo));
    if (!run || !runPlayableForFobType(card, run, type)) return "run";
    return "ok";
}

/**
 * Kapanan koşunun yerine geçecek koşu: numara sırasında MEVCUTTAN SONRAKİ ilk oynanabilir
 * koşu; ileride yoksa geriye doğru ilk oynanabilir. Hiçbiri yoksa null (kademe yükselir).
 */
export function nextPlayableFobRun(card: any, type: any, currentRunNo: any): any {
    const runs = [...(card?.runs ?? [])].sort((a: any, b: any) => Number(a.no) - Number(b.no));
    const current = Number(currentRunNo) || 0;
    let before: any = null;
    for (const run of runs) {
        if (!runPlayableForFobType(card, run, type)) continue;
        if (Number(run.no) > current) return run;
        if (!before) before = run;
    }
    return before;
}

// --- karta giriş ---------------------------------------------------------------------
//
// Karta GİRİŞ bir kapanma değil: kullanıcı orada bir koşuya bakmıyordu, uyarılacak bir şey
// yok. Bu yüzden açılış doğrudan oynanabilir bir yere yapılır; guard'a söyleyecek bir şey
// bırakılmaz. "Tarifedeki ilk koşu" yeterli değil: kapanan koşu bir-iki tick `runs`ta
// kalabiliyor. Mobil karşılığı: ekuri-mobile `initialFobRun`.

/**
 * Karta girişte bakılacak koşu: tercih edilen koşu oynanabilirse o, değilse ondan SONRAKİ
 * ilk oynanabilir koşu; tercih yoksa ilk oynanabilir koşu. Hiçbiri yoksa null.
 */
export function initialFobRun(card: any, type: any, preferredRunNo?: any): any {
    if (preferredRunNo != null) {
        const preferred = (card?.runs ?? []).find((r: any) => Number(r.no) === Number(preferredRunNo));
        if (preferred && runPlayableForFobType(card, preferred, type)) return preferred;
    }
    return nextPlayableFobRun(card, type, preferredRunNo ?? null);
}

/**
 * Karta girişte seçilecek tür (`types`: kartın açtığı seçilebilir FOB türleri, katalog
 * sırasıyla): tercih edilen tür oynanabilir koşu taşıyorsa o, değilse oynanabilir koşusu
 * olan ilk tür. Hiçbir türde oynanacak koşu yoksa eski davranış (tercih, yoksa ilk tür) -
 * o durumda kart kademesi devreye girer.
 */
export function initialFobType(card: any, types: any[], preferredTypeId?: any): any {
    const hasRun = (type: any) => initialFobRun(card, type) != null;
    const preferred = preferredTypeId == null
        ? undefined
        : types.find((t: any) => Number(t.id) === Number(preferredTypeId));
    if (preferred && hasRun(preferred)) return preferred;
    return types.find(hasRun) ?? preferred ?? types[0] ?? null;
}

/** Kartta içeriği kalan tür: mevcut tür oynanıyorsa KORUNUR, yoksa ilk uygun tür. */
export function nextFobTypeWithContent(card: any, current: any): any {
    const hasContent = (type: any) =>
        (card?.runs ?? []).some((run: any) => runPlayableForFobType(card, run, type));
    if (current && hasContent(current)) return current;
    for (const wager of card?.wager_details ?? []) {
        const bt: any = BettingTypesMap[Number(wager.type)];
        if (!bt?.isFob || !bt?.isSelectable) continue;
        if (hasContent(bt)) return bt;
    }
    return null;
}

/** Kartın kendisi düştüğünde geçilecek kart: listedeki ilk oynanabilir kart. */
export function nextFobCard(fobCards: any[], currentId: any): any {
    for (const card of fobCards) {
        if (card._id === currentId) continue;
        if (nextFobTypeWithContent(card, null)) return card;
    }
    return null;
}

// --- kullanıcıya gösterilecek metinler -------------------------------------------------

/**
 * Modal gövdesi `innerHtml` bayrağı verilmezse ORTALANMIŞ tek `text-small` paragraf olarak
 * basılıyor ve uzun metin okunmuyordu. Satırlar ayrı paragraflara çevriliyor; çağıran
 * `innerHtml: true` geçmek zorunda.
 */
export function asModalHtml(text: string): string {
    return text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => `<p class="text-small text-center mb-1">${line}</p>`)
        .join("");
}

/** Müşterek modalının gövdesi. Kademe adı değil, kapanan ŞEYİN adı gösterilir. */
export function mutualBreakMessage(params: {
    brk: SelectionBreak;
    cardName?: string;
    typeName?: string;
    subTypeId?: any;
    clearedCount?: number;
}): string {
    const {brk, cardName, typeName, subTypeId, clearedCount = 0} = params;
    const lines: string[] = [];
    if (brk === "run") {
        lines.push(`${subTypeId ?? "-"}. koşu için bahisler kapandı.`);
    } else if (brk === "type") {
        lines.push(`${typeName ?? "Seçili"} bahsi kapandı.`);
    } else if (brk === "card") {
        lines.push(`${cardName ?? "Seçili hipodrom"} kartındaki bahisler kapandı.`);
    } else {
        return "";
    }
    if (clearedCount > 0) {
        lines.push(`Kuponunuzdaki ${clearedCount} seçim temizlendi.`);
    }
    lines.push("Güncel bültene dönülüyor.");
    return lines.join("\n");
}

/** Sabit ihtimallide geçişin bildirimi. */
export function fobMoveNotice(params: {
    brk: SelectionBreak;
    typeName?: string;
    toRunNo?: any;
    toCardName?: string;
}): string {
    const {brk, typeName, toRunNo, toCardName} = params;
    if (brk === "run") {
        return toRunNo == null
            ? "Bu koşuda sabit ihtimalli bahis kapandı."
            : `Bu koşuda sabit ihtimalli bahis kapandı.\n${toRunNo}. koşuya geçildi.`;
    }
    if (brk === "type") {
        return `${typeName ?? "Seçili bahis türü"} kapandı.\nAçık olan bahis türüne geçildi.`;
    }
    if (brk === "card") {
        return toCardName == null
            ? "Bu hipodromda sabit ihtimalli bahis kalmadı."
            : `Bu hipodromda sabit ihtimalli bahis kalmadı.\n${toCardName} kartına geçildi.`;
    }
    return "";
}

// --- ardışık koşu zinciri (Ganyan → 7'li) -----------------------------------------------

// Bir ayak koşulunca müşterek kuponu sıfırlayıp kullanıcıyı bültenin dışına atmak yerine,
// kuponu BOŞALTIP zincirde bir alt basamağa bırakıyoruz:
// 7'li → 6'lı → 5'li → 4'lü → 3'lü → Çifte → Ganyan.
//
// Kupon TAŞINMIYOR. Taşımak, kullanıcının o bahis için kurmadığı bir kuponu ona aitmiş gibi
// göstermek olurdu; eksik ya da hatalı taşıma kafa karıştırır.
//
// Havuz yapısı karttan karta değişiyor, bu yüzden zincir KODLANMIYOR, tarifede ARANIYOR:
//   * İzmir (7 koşu, iç içe): 7'li(1-7) → 6'lı(2-7) → ... → Çifte(6-7) → Ganyan(7)
//   * Saratoga: iki paralel zincir, 6'lı[1,5] ve 5'li[2,6] gibi çok gruplu türler
//   * Ankara: 6'lı(1-6) + 5'li(4-8) - ÖRTÜŞEN ama iç içe DEĞİL
//   * Karma: 6'lı(1-6) ve Ganyan, ara basamak hiç yok
// Hiçbir zincir türü kalmamışsa çağıran bugünkü sıfırlama modalını gösterir.

/** Zincire girebilen tür. Ölçüt katalogdaki `isLegChain` bayrağı. */
export function isChainableLegType(type: any): boolean {
    return type?.isLegChain === true;
}

/** Alt tür etiketinden ("3-4-5-6-7") ayak numaraları. */
export function groupRunNos(label: any): number[] {
    // Boş parçalar ÖNCE eleniyor: `Number("")` sıfır döndürüyor ve etiket boşken hayalî bir
    // "0. ayak" üretiyordu (Dart'ta `int.tryParse` bunu kendiliğinden eliyor).
    return String(label ?? "")
        .split("-")
        .map((p) => p.trim())
        .filter((p) => p !== "")
        .map((p) => Number(p))
        .filter((n) => Number.isFinite(n));
}

/** Zincirde bir alt basamak. */
export type DegradeTarget = {
    /** Hedef tür (katalog nesnesi + kartın `runs`u). */
    type: any;
    /** Hedef grubun başlangıç ayağı = yeni alt tür id'si. */
    subTypeId: number;
    /** Hedef grubun ayakları. */
    runs: number[];
    /** Kapanan ayak: mevcut grubun İLKİ. Metinde kullanılıyor. */
    closedRunNo: number;
    /** Sayfaların beklediği alt tür biçimi. */
    subType: {id: number; label: string};
};

/**
 * Zincirde bir alt basamak: bir alt tür (kartta yoksa bir alttaki), o türün de bulunulan
 * gruba EN ÇOK ayak paylaşan grubu; eşitlikte en erken başlayan. Yoksa null.
 */
export function degradeTarget(params: {
    brk: SelectionBreak;
    card: any;
    currentType: any;
    groupRuns: number[];
}): DegradeTarget | null {
    const {brk, card, currentType, groupRuns} = params;
    // KART kademesi kırıldıysa zincir DENENMEZ: kapalı kart `wager_details` taşımaya devam
    // edebiliyor, o yüzden burada hedef "bulunur" ve kullanıcı kapanmış kartın üstünde
    // bırakılırdı. Karma hipodromu tam olarak bu: bir koşu başlayınca kart komple kapanıyor,
    // yani kırılma run/type değil CARD seviyesinde geliyor ve doğru davranış sonraki
    // hipodroma geçmek. Bayrak parametre olarak isteniyor ki çağıran unutamasın.
    if (brk === "card") return null;
    if (!card || !isChainableLegType(currentType)) return null;
    if (groupRuns.length < 2) return null; // Ganyan zincirin sonu
    const closed = groupRuns[0];

    // Tür KART VERİSİYLE döndürülüyor (`buildMutualBettingTypes` ile aynı biçim: katalog +
    // kartın fiyatı + kartın koşuları). Ham katalog nesnesi dönseydi çağıran tarafta birim
    // fiyat ve `runs` kaybolurdu.
    const candidates = (card.wager_details ?? [])
        .map((w: any) => {
            const catalog: any = BettingTypesMap[Number(w.type)];
            if (!catalog) return null;
            const runs = wagerRunNos(w);
            return {type: {...catalog, price: w.price ?? catalog.price, runs}, runs};
        })
        .filter((c: any) =>
            c && isChainableLegType(c.type) && Number(c.type.column) < groupRuns.length)
        .sort((a: any, b: any) => Number(b.type.column) - Number(a.type.column));

    for (const candidate of candidates) {
        const step = Number(candidate.type.subType ?? 1);
        let best: DegradeTarget | null = null;
        let bestOverlap = -1;
        for (const start of candidate.runs) {
            const group = Array.from({length: step}, (_, i) => start + i);
            // Kapanan ayağı içeren grup ASLA hedef olamaz. Sunucu tarifeyi kırpıyor ama buna
            // güvenmiyoruz: kullanıcıyı koşulmuş bir ayağın üstüne bırakmak olurdu.
            if (group.includes(closed)) continue;
            const overlap = group.filter((r) => groupRuns.includes(r)).length;
            if (overlap > bestOverlap) {
                bestOverlap = overlap;
                best = {
                    type: candidate.type,
                    subTypeId: start,
                    runs: group,
                    closedRunNo: closed,
                    subType: {id: start, label: group.join("-")},
                };
            }
        }
        if (best) return best;
    }
    return null;
}

/**
 * Zincirde bir alt basamağa geçişin modal metni. Kupon TAŞINMIYOR, boşaltılıyor - metin de
 * bunu birebir söylüyor, "taşındı" gibi bir ima bırakmıyor.
 */
export function degradeMessage(params: {
    closedRunNo: number;
    fromTypeName: string;
    toTypeName: string;
    clearedCount?: number;
}): string {
    const {closedRunNo, fromTypeName, toTypeName, clearedCount = 0} = params;
    const lines = [`${closedRunNo}. koşu için bahisler kapandı, ${fromTypeName} sona erdi.`];
    lines.push(clearedCount > 0
        ? `Kuponunuz temizlendi, ${toTypeName} bahsine geçildi.`
        : `${toTypeName} bahsine geçildi.`);
    return lines.join("\n");
}

// --- kupon satırının neden oynanamadığı --------------------------------------------------

/**
 * Bir seçim İKİ ayrı sebeple oynanamaz olur ve ikisi farklı şeyler:
 *   * `run`   - koşu kapandı. Bunu zincir/sıfırlama akışı yakalıyor (kırılma tetikleniyor,
 *               kupon boşalıyor). Geriye yalnız dar bir pencere kalıyor: sunucu `run.status`ü
 *               `wager_details` kırpmasından ÖNCE güncellerse bir-iki tick boyunca koşu
 *               kapalı ama alt tür hâlâ listede olur.
 *   * `horse` - AT KOŞUDAN ÇEKİLDİ. Kart, tür ve koşu açık; seçim bağlamında kırılan hiçbir
 *               şey yok, yani guard hiç çalışmaz. Kupondaki bacak yine de geçersizdir ve
 *               "Oyna" kilitlenir - bu durumu kullanıcıya söyleyen tek yer uyarı şeridi.
 */
export type MutualStaleReason = "run" | "horse";

export function mutualStaleReason(run: any, horseNo: any): MutualStaleReason | null {
    if (!run || Number(run.status) !== 0) return "run";
    // "s" tek bir ata değil "bu ayaktaki tüm atlar"a karşılık geliyor; koşu açıksa geçerli.
    if (String(horseNo) === "s") return null;
    const horse = run.horses?.find((h: any) => String(h.no) === String(horseNo));
    return !horse || horse.run_status === false ? "horse" : null;
}

/** Uyarıda gösterilecek özet: at çekilmesinde KOŞU + AT NUMARALARI, kapanmada koşu numaraları. */
export type StaleSummary = {
    withdrawn: {run: number; horses: string[]}[];
    closedRuns: number[];
};

export function summarizeStale(
    rows: {run: any; horse: any; reason: MutualStaleReason | null}[]
): StaleSummary {
    const byRun = new Map<number, string[]>();
    const closed = new Set<number>();
    for (const row of rows) {
        const run = Number(row.run);
        if (!Number.isFinite(run)) continue;
        if (row.reason === "horse") {
            const horses = byRun.get(run) ?? [];
            if (!horses.includes(String(row.horse))) horses.push(String(row.horse));
            byRun.set(run, horses);
        } else if (row.reason === "run") {
            closed.add(run);
        }
    }
    return {
        withdrawn: Array.from(byRun.entries())
            .sort((a: [number, string[]], b: [number, string[]]) => a[0] - b[0])
            .map(([run, horses]: [number, string[]]) => ({
                run,
                horses: horses.sort((a: string, b: string) => Number(a) - Number(b)),
            })),
        closedRuns: Array.from(closed).sort((a: number, b: number) => a - b),
    };
}

/** "3. koşuda 1 numaralı at koşmaz durumda" / "4. koşu kapandı" - ikisi varsa ikisi de. */
export function staleSummaryText(summary: StaleSummary): string {
    const parts: string[] = [];
    if (summary.withdrawn.length > 0) {
        parts.push(
            summary.withdrawn
                .map((g) => `${g.run}. koşuda ${g.horses.join(", ")} numaralı at`)
                .join(", ") + " koşmaz durumda"
        );
    }
    if (summary.closedRuns.length > 0) {
        parts.push(`${summary.closedRuns.join(", ")}. koşu kapandı`);
    }
    return parts.join(" · ");
}
